import { useEffect, useState } from 'react';
import { fmtMoney } from '@/lib/domain/format';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { useToast } from '@/components/ui/toast';
import { firstValidationMessage, type ApiError } from '@/lib/api/client';
import { useOriginadoraDrawer } from './drawerStores';
import { useOriginatorProfile, useUpdateOriginatorProfile } from './profileApi';
import { InfoRow, MetricsStrip, ProfileDealsList, ProfileDrawerShell } from './drawerShared';

const INPUT = 'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink outline-none';
const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

/** Drawer da ficha da Originadora (DOCUMENTACAO.md §4.4): dados + métricas + deals trazidos + edição. */
export function OriginadoraDrawer() {
  const id = useOriginadoraDrawer((s) => s.id);
  const close = useOriginadoraDrawer((s) => s.close);
  if (!id) return null;
  return <OriginadoraDrawerInner id={id} onClose={close} />;
}

function OriginadoraDrawerInner({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, isError } = useOriginatorProfile(id);
  const ref = useReferenceData();
  const update = useUpdateOriginatorProfile();
  const toast = useToast();
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState<number | ''>('');
  const [vegaRm, setVegaRm] = useState('');
  const [summary, setSummary] = useState('');

  // Pré-preenche o form de edição quando entra em modo edição (ou troca de ficha).
  useEffect(() => {
    if (data && editing) {
      setName(data.name);
      setTypeId(data.typeId);
      setVegaRm(data.vegaRelationshipManagerId ?? '');
      setSummary(data.summary ?? '');
    }
  }, [editing, data]);

  const typeName = (t: number): string => ref.data?.originatorTypes.find((x) => x.id === t)?.name ?? '—';
  const userName = (uid: string | null): string =>
    uid ? (ref.data?.users.find((u) => u.id === uid)?.name ?? '—') : '—';

  const save = () => {
    if (!data || name.trim() === '' || typeId === '') return;
    update.mutate(
      {
        id,
        eTag: data.eTag,
        name: name.trim(),
        typeId: Number(typeId),
        vegaRelationshipManagerId: vegaRm || null,
        summary: summary.trim() || null,
      },
      {
        onSuccess: () => {
          toast('Originadora atualizada');
          setEditing(false);
        },
        onError: (err) => {
          const status = (err as ApiError).status;
          toast(status === 412 ? 'Conflito: a ficha mudou. Reabra e tente de novo.' : (firstValidationMessage(err) ?? 'Erro ao salvar.'), 'danger');
        },
      },
    );
  };

  return (
    <ProfileDrawerShell
      kindLabel="Originadora"
      title={data?.name ?? (isLoading ? 'Carregando…' : 'Originadora')}
      subtitle={data ? typeName(data.typeId) : undefined}
      onClose={onClose}
      onEdit={data ? () => setEditing(true) : undefined}
      editing={editing}
    >
      {isError ? (
        <div className="py-6 text-[13px] text-vega-danger">Erro ao carregar a ficha.</div>
      ) : !data ? (
        <div className="py-6 text-[13px] text-vega-muted">Carregando…</div>
      ) : editing ? (
        <div className="space-y-3">
          <div>
            <span className={LABEL}>Nome *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
          </div>
          <div>
            <span className={LABEL}>Tipo *</span>
            <select value={typeId} onChange={(e) => setTypeId(e.target.value === '' ? '' : Number(e.target.value))} className={INPUT}>
              <option value="">Selecione…</option>
              {ref.data?.originatorTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <span className={LABEL}>Responsável Vega</span>
            <select value={vegaRm} onChange={(e) => setVegaRm(e.target.value)} className={INPUT}>
              <option value="">—</option>
              {ref.data?.users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <span className={LABEL}>Resumo</span>
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className={INPUT} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setEditing(false)} className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] text-vega-muted">
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={update.isPending || name.trim() === '' || typeId === ''}
              className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
            >
              {update.isPending ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <MetricsStrip
            items={[
              { label: 'Deals trazidos', value: String(data.metrics.dealsBrought) },
              {
                label: 'Conv. IC1',
                value:
                  data.metrics.dealsBrought > 0
                    ? `${Math.round((data.metrics.reachedIc1Count / data.metrics.dealsBrought) * 100)}%`
                    : '—',
              },
              { label: 'Fechados', value: String(data.metrics.closedCount) },
              { label: 'Exposição', value: fmtMoney(data.metrics.exposureBRL) },
            ]}
          />
          <div className="mb-5">
            <InfoRow label="Tipo">{typeName(data.typeId)}</InfoRow>
            <InfoRow label="Responsável Vega">{userName(data.vegaRelationshipManagerId)}</InfoRow>
            <InfoRow label="Resumo">{data.summary || '—'}</InfoRow>
          </div>
          <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-vega-muted">Deals trazidos</h3>
          <ProfileDealsList deals={data.deals} />
        </>
      )}
    </ProfileDrawerShell>
  );
}
